<script>
import { isStatusUpdateDateValid } from '@/utils/statusUpdateDateUtils'

export default {
  name: 'CustomDateInput',
  props: {
    modelValue: {
      required: true
    },
    mode: {
      type: String,
      required: false,
      default: 'single'
    },
    minDate: {
      required: false,
      default: null
    },
    maxDate: {
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hideDetails: {
      type: [Boolean, String],
      default: true
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    placeholder: {
      type: String,
      required: false,
      default: null
    },
    rules: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      localModel: this.modelValue,
      manualInputValue: this.modelValue
    }
  },
  computed: {
    isRangeMode() {
      return this.mode === 'range'
    }
  },
  methods: {
    emitValue(value) {
      this.localModel = value
      this.$emit('update:modelValue', value)
    },
    handleClearValue() {
      const clearedValue = this.isRangeMode ? [] : null
      this.emitValue(clearedValue)
      this.manualInputValue = clearedValue
    },
    handleDateUpdate(value) {
      const validValue = this.isRangeMode || this.isDateValid(value) ? value : null
      this.emitValue(validValue)
      this.manualInputValue = validValue
    },
    handleManualInput(event) {
      if (this.isRangeMode || this.disabled) return

      const inputValue = event.target.value
      this.manualInputValue = inputValue

      const cleanInput = inputValue.replace(/\D/g, '')

      if (cleanInput === '') {
        this.emitValue(null)
        return
      }

      if (cleanInput.length !== 8) return

      const parsedDate = this.parseManualDate(inputValue)

      if (parsedDate && this.isDateValid(parsedDate)) {
        this.emitValue(parsedDate)
      } else {
        this.emitValue(null)
      }
    },
    handleBlur() {
      if (this.isRangeMode || this.disabled) return

      let input = this.manualInputValue

      if (input instanceof Date) {
        if (this.isDateValid(input)) {
          this.emitValue(input)
        } else {
          this.emitValue(null)
        }
        return
      }

      const inputStr = String(input)
      const cleanInput = inputStr.replace(/\D/g, '')

      if (cleanInput.length !== 8) {
        this.emitValue(null)
        return
      }

      const parsedDate = this.parseManualDate(inputStr)

      if (!parsedDate || !this.isDateValid(parsedDate)) {
        this.emitValue(null)
      } else {
        this.emitValue(parsedDate)
      }
    },
    parseManualDate(input) {
      if (!input || typeof input !== 'string') return null

      const cleanInput = input.replace(/\D/g, '')

      if (cleanInput.length !== 8) {
        return null
      }

      const day = parseInt(cleanInput.substring(0, 2))
      const month = parseInt(cleanInput.substring(2, 4))
      const year = parseInt(cleanInput.substring(4, 8))

      if (month < 1 || month > 12) return null
      if (day < 1 || day > 31) return null

      const date = new Date(year, month - 1, day)

      if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return null
      }

      return date
    },

    isDateValid(date) {
      return isStatusUpdateDateValid(date, this.minDate, this.maxDate)
    }
  },
  watch: {
    modelValue(newValue) {
      this.localModel = newValue
      this.manualInputValue = newValue
    }
  }
}
</script>

<template>
  <v-date-input
    v-model="localModel"
    :label="label || $t('COMMON.DATE_PLACEHOLDER')"
    :placeholder="placeholder || $t('COMMON.DATE_PLACEHOLDER')"
    :clearable="clearable"
    :disabled="disabled"
    :rules="rules"
    :multiple="isRangeMode ? 'range' : undefined"
    :hide-details="hideDetails"
    @input="handleManualInput"
    @update:model-value="handleDateUpdate"
    @click:clear="handleClearValue"
    @blur="handleBlur"
    :min="minDate"
    :max="maxDate"
  />
</template>
